import { useState } from 'react';
import type { AcceptedAddress, EmailAdmin, MailboxInfo, RejectedForward } from '@shared/gmail';
import type { TeamMember } from '@shared/types';
import { ApiError, api, errorMessage } from '@/lib/api';
import { daysFromToday, formatDate, formatNumber, plural } from '@/lib/format';
import { useTeam } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import { Avatar } from '@/ui/Avatar';
import { Callout, Chip, Empty } from '@/ui/bits';
import { CopyField, copyWithToast } from '@/ui/Copy';
import { confirmDialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import { connectUrl, firstName, goConnect, refreshInboxes } from './queries';

// Shared inboxes and the CRM address in the "Email & inboxes" admin section.

const weekdayFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });

/** "today", "yesterday", "on Wed" within the week, "on 12 Oct" before. */
function onDay(at: string): string {
  const d = daysFromToday(at);
  if (d === 0) return 'today';
  if (d === -1) return 'yesterday';
  if (d > -7) return `on ${weekdayFmt.format(new Date(at))}`;
  return `on ${formatDate(at)}`;
}

export async function disconnectMailbox(mb: MailboxInfo, what: string): Promise<void> {
  const ok = await confirmDialog({
    title: `Disconnect ${what}?`,
    body: `Clientbook stops reading ${mb.address} right away and gives the access back to Google. Emails already on records stay.`,
    confirmLabel: 'Disconnect',
    cancelLabel: 'Keep connected',
    icon: 'logout',
    danger: true,
    action: async () => {
      await api.post(`/gmail/mailboxes/${mb.id}/disconnect`);
      await refreshInboxes();
    },
  });
  if (ok) toast(`${mb.address} is disconnected`, { icon: 'logout' });
}

/** Asks, then disconnects your own inbox, from the admin section or your account settings. Resolves true once done. */
export async function disconnectMine(mb: MailboxInfo, configured: boolean): Promise<boolean> {
  const ok = await confirmDialog({
    title: 'Disconnect your inbox?',
    body: `Clientbook stops reading replies in ${mb.address} right away. Emails already on records stay.`,
    confirmLabel: 'Disconnect',
    cancelLabel: 'Keep connected',
    icon: 'logout',
    danger: true,
    action: async () => {
      await api.post('/gmail/me/disconnect');
      await refreshInboxes();
    },
  });
  if (ok) {
    toast('Your inbox is disconnected', {
      icon: 'logout',
      action: configured && mb.live ? { label: 'Reconnect', run: () => goConnect('personal') } : undefined,
    });
  }
  return ok;
}

function MailboxMenu({ mb, kind, configured, what }: { mb: MailboxInfo; kind: 'shared' | 'crm'; configured: boolean; what: string }) {
  const pop = usePopover();
  const live = mb.status !== 'disconnected';
  if (!configured && !live) return null;
  return (
    <>
      <button type="button" className="icon-btn sm" aria-label={`Options for ${mb.address}`} data-tip="Options" {...pop.anchorProps}>
        <Icon name="more" />
      </button>
      <Popover state={pop} label={`Options for ${mb.address}`} width={230}>
        {configured ? (
          <MenuItem icon="refresh" onSelect={() => goConnect(kind, mb.address)}>
            {mb.live ? 'Reconnect' : 'Connect with Google'}
          </MenuItem>
        ) : null}
        <MenuItem icon="copy" onSelect={() => void copyWithToast(mb.address, 'Address')}>
          Copy address
        </MenuItem>
        {live ? (
          <>
            <MenuSep />
            <MenuItem icon="logout" danger onSelect={() => disconnectMailbox(mb, what)}>
              Disconnect
            </MenuItem>
          </>
        ) : null}
      </Popover>
    </>
  );
}

function mailboxState(mb: MailboxInfo): { label: string; tone: 'success' | 'danger' | 'plain' } {
  if (mb.status === 'disconnected') return { label: 'Disconnected', tone: 'plain' };
  if (mb.status === 'error') return { label: 'Needs reconnecting', tone: 'danger' };
  if (!mb.live) return { label: 'Sample', tone: 'plain' };
  return { label: 'Connected', tone: 'success' };
}

function sharedLine(mb: MailboxInfo, byId: Map<string, TeamMember>): string {
  if (mb.status === 'disconnected') {
    return mb.disconnectedAt ? `Disconnected ${formatDate(mb.disconnectedAt)} · ${plural(mb.emailCount, 'email')} kept` : 'Disconnected';
  }
  if (mb.status === 'error') return mb.statusDetail ?? 'Google stopped accepting Clientbook’s access';
  const who = mb.userId ? byId.get(mb.userId) : undefined;
  const parts = [who ? `Connected by ${firstName(who.name)}` : 'Connected', 'tracks everything'];
  if (mb.emailCount > 0) parts.push(plural(mb.emailCount, 'email'));
  if (!mb.live) parts.splice(1, 0, 'sample');
  return parts.join(' · ');
}

export function SharedBox({ data }: { data: EmailAdmin }) {
  const { byId } = useTeam();
  const configured = data.google.configured;
  const list = data.shared;
  return (
    <div className="tile span-6">
      <div className="tile-h">
        <h3>Shared inboxes</h3>
        {list.length ? <span className="cnt">{formatNumber(list.filter((m) => m.status !== 'disconnected').length)} connected</span> : null}
      </div>
      {list.length === 0 ? (
        <Empty compact icon="users" title="No shared inboxes yet">
          Shared inboxes like hello@ track every email, from anyone. Unknown senders wait in Inbox triage.
        </Empty>
      ) : (
        list.map((mb) => {
          const st = mailboxState(mb);
          return (
            <div key={mb.id} className={mb.status === 'disconnected' ? 'inbox-row gm-off' : 'inbox-row'}>
              <span className="svc ggl" aria-hidden="true">
                <Icon name="mail" />
              </span>
              <div className="grow">
                <b>{mb.address}</b>
                <span>{sharedLine(mb, byId)}</span>
              </div>
              <Chip tone={st.tone} icon={st.tone === 'danger' ? 'x-circle' : undefined}>
                {st.label}
              </Chip>
              <MailboxMenu mb={mb} kind="shared" configured={configured} what="this shared inbox" />
            </div>
          );
        })
      )}
      {configured ? (
        <div className="gm-connect">
          <a className="btn btn-sm btn-tile" href={connectUrl('shared')}>
            <Icon name="plus" />
            Connect a shared inbox
          </a>
          <span className="hint">Sign in to Google as the shared inbox itself.</span>
        </div>
      ) : (
        <p className="gm-note">Shared inboxes connect once Google is set up in the guide.</p>
      )}
      <div className="tile-h gm-box-h2">
        <h3>Sending</h3>
      </div>
      <p className="gm-text">
        Gmail allows 2,000 emails per user per day on Google Workspace. List sends are spaced out automatically to stay well
        inside it.
      </p>
    </div>
  );
}

export function CrmBox({ data }: { data: EmailAdmin }) {
  const { byId } = useTeam();
  const configured = data.google.configured;
  const mb = data.crm;
  const [added, setAdded] = useState<{ email: string; name: string; kind: string } | null>(null);
  const [allRejected, setAllRejected] = useState(false);
  const shown = allRejected ? data.rejected : data.rejected.slice(0, 3);
  const rest = data.rejected.length - shown.length;

  return (
    <div className="tile span-6">
      <div className="tile-h">
        <h3>Forwarding to the CRM</h3>
        {data.rejectedThisWeek > 0 ? <span className="cnt">{formatNumber(data.rejectedThisWeek)} rejected this week</span> : null}
      </div>

      {mb && mb.status !== 'disconnected' ? (
        <>
          <div className="gm-crm-field">
            <CopyField value={mb.address} what="CRM address" />
            <MailboxMenu mb={mb} kind="crm" configured={configured} what="the CRM address" />
          </div>
          {mb.status === 'error' ? (
            <Callout tone="danger" className="gm-gap" title="Google stopped accepting Clientbook’s access" role="alert">
              {mb.statusDetail ?? 'Forwards wait in Gmail until the address is reconnected.'}
            </Callout>
          ) : !mb.live ? (
            <p className="gm-note">Sample address. Forwards start arriving once you connect the real CRM address with Google.</p>
          ) : null}
        </>
      ) : (
        <Empty
          compact
          icon="forward"
          title={mb ? `${mb.address} was disconnected ${formatDate(mb.disconnectedAt ?? mb.connectedAt ?? new Date().toISOString())}` : 'No CRM address yet'}
          action={
            configured ? (
              <a className="btn btn-sm btn-tile" href={connectUrl('crm', mb?.address)}>
                <Icon name="link" />
                {mb ? 'Reconnect the CRM address' : 'Connect the CRM address'}
              </a>
            ) : undefined
          }
        >
          Teammates forward or copy emails to one Google address, and Clientbook files them on the right people, companies and deals.
          {configured ? null : ' The CRM address connects once Google is set up in the guide.'}
        </Empty>
      )}

      <AcceptedList accepted={data.accepted} byId={byId} highlight={added?.email ?? null} />

      {added ? (
        <Callout tone="success" className="gm-gap" role="status" title={`${added.email} now forwards to the CRM`}>
          Listed against {added.name} as a {added.kind} address.
        </Callout>
      ) : null}

      {shown.map((r) => (
        <RejectedCallout key={r.id} r={r} onAdded={setAdded} />
      ))}
      {rest > 0 ? (
        <button type="button" className="btn btn-sm btn-ghost gm-more" onClick={() => setAllRejected(true)}>
          Show {plural(rest, 'more turned-away forward')}
        </button>
      ) : null}
    </div>
  );
}

function AcceptedList({ accepted, byId, highlight }: { accepted: AcceptedAddress[]; byId: Map<string, TeamMember>; highlight: string | null }) {
  if (accepted.length === 0) {
    return <p className="gm-note">Forwards are accepted when Gmail confirms they come from an address listed against an active member. None are listed yet.</p>;
  }
  return (
    <>
      <p className="gm-lead">
        {accepted.length === 1
          ? 'Accepts mail that Gmail confirms comes from the one address listed against an active member:'
          : `Accepts mail that Gmail confirms comes from one of the ${formatNumber(accepted.length)} addresses listed against active members:`}
      </p>
      <div className="tags gm-tags">
        {accepted.map((a) => {
          const m = byId.get(a.userId);
          const on = highlight !== null && a.email.toLowerCase() === highlight.toLowerCase();
          return (
            <span key={a.email} className={on ? 'tag gm-added' : 'tag'} title={m ? `${m.name} · ${a.kind}` : a.kind}>
              {m ? <Avatar name={m.name} hue={m.hue} size="xs" /> : null}
              {a.email}
              {a.kind === 'private' ? <span className="muted gm-kind">private</span> : null}
            </span>
          );
        })}
      </div>
    </>
  );
}

function RejectedCallout({ r, onAdded }: { r: RejectedForward; onAdded: (a: { email: string; name: string; kind: string }) => void }) {
  const { active, byId } = useTeam();
  const suggested = r.suggestedUserId ? byId.get(r.suggestedUserId) : undefined;
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState<'add' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const other = usePopover();

  const add = async (userId: string) => {
    if (busy || !userId) return;
    setBusy('add');
    setError(null);
    try {
      const res = await api.post<{ email: string; kind: 'work' | 'private'; name: string }>(`/gmail/admin/rejected/${r.id}/add`, { userId });
      onAdded({ email: res.email, name: res.name, kind: res.kind });
      await refreshInboxes();
    } catch (err) {
      setError(err instanceof ApiError && err.fields.userId ? err.fields.userId : errorMessage(err));
      setBusy(null);
    }
  };

  const dismiss = async () => {
    if (busy) return;
    setBusy('dismiss');
    const key = ['admin', 'email'];
    const before = queryClient.getQueryData<EmailAdmin>(key);
    queryClient.setQueryData<EmailAdmin>(key, (d) => (d ? { ...d, rejected: d.rejected.filter((x) => x.id !== r.id) } : d));
    try {
      await api.post(`/gmail/admin/rejected/${r.id}/dismiss`);
      toast(`Forward from ${r.fromAddr} dismissed`, { icon: 'check' });
    } catch (err) {
      if (before) queryClient.setQueryData(key, before);
      setBusy(null);
      toastError(err);
    }
  };

  const candidates = active.filter((m) => m.id !== suggested?.id);

  if (r.reason === 'unverified') {
    return (
      <Callout tone="plain" icon="info" className="gm-gap gm-rej">
        <span>
          A forward from <b>{r.fromAddr}</b> was turned away {onDay(r.receivedAt)}: Gmail could not confirm that it really came from that address.
        </span>
        {r.subject ? <span className="gm-rej-sub">“{r.subject}”</span> : null}
        <div className="acts">
          <button type="button" className="btn btn-sm btn-ghost" onClick={dismiss} disabled={!!busy}>
            Dismiss
          </button>
        </div>
      </Callout>
    );
  }

  return (
    <Callout tone="plain" icon="info" className="gm-gap gm-rej">
      <span>
        A forward from <b>{r.fromAddr}</b> was turned away {onDay(r.receivedAt)}: the address belongs to no team member.{' '}
        {suggested ? `Add it to ${suggested.name} to accept forwards from it.` : 'Add it to the teammate it belongs to, to accept forwards from it.'}
      </span>
      {r.subject ? <span className="gm-rej-sub">“{r.subject}”</span> : null}
      {error ? (
        <p className="field-err" role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      <div className="acts">
        {suggested ? (
          <>
            <button type="button" className="btn btn-sm btn-tile" onClick={() => add(suggested.id)} disabled={!!busy} aria-busy={busy === 'add' || undefined}>
              <Icon name="user-plus" />
              {busy === 'add' ? 'Adding' : `Add to ${suggested.name}'s addresses`}
            </button>
            {candidates.length ? (
              <>
                <button type="button" className="btn btn-sm btn-ghost" disabled={!!busy} {...other.anchorProps}>
                  Someone else
                </button>
                <Popover state={other} label="Add to a teammate" width={240} align="left">
                  {candidates.map((m) => (
                    <MenuItem key={m.id} lead={<Avatar name={m.name} hue={m.hue} size="xs" />} onSelect={() => add(m.id)}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Popover>
              </>
            ) : null}
          </>
        ) : (
          <form
            className="gm-rej-pick"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pick) return setError('Choose the teammate this address belongs to.');
              void add(pick);
            }}
          >
            <select className="select select-sm" aria-label={`Teammate for ${r.fromAddr}`} value={pick} onChange={(e) => setPick(e.target.value)} disabled={!!busy}>
              <option value="">Choose a teammate</option>
              {active.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <button type="submit" className="btn btn-sm btn-tile" disabled={!!busy} aria-busy={busy === 'add' || undefined}>
              <Icon name="user-plus" />
              {busy === 'add' ? 'Adding' : 'Add address'}
            </button>
          </form>
        )}
        <button type="button" className="btn btn-sm btn-ghost" onClick={dismiss} disabled={!!busy}>
          Dismiss
        </button>
      </div>
    </Callout>
  );
}
